/**
 * Push Notification Service for Firebase Cloud Messaging
 *
 * Handles permission requests, token registration, and foreground notifications.
 * Works with the firebase-messaging-sw.js service worker for background notifications.
 */

import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';

// VAPID key for web push - from Firebase Console > Cloud Messaging > Web Push certificates
// This is a public key used to authenticate the sender
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

// API base URL (REACT_APP_API_URL includes /api suffix, e.g. https://example.com/api)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Notification Service singleton for managing push notifications
 */
class NotificationService {
  private messaging: Messaging | null = null;
  private currentToken: string | null = null;
  private isInitialized = false;

  /**
   * Check if push notifications are supported in this browser
   */
  isSupported(): boolean {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission and initialize FCM
   * Returns true if permission granted
   */
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      console.log('NotificationService: Push notifications not supported');
      return false;
    }

    try {
      // Request permission
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        console.log('NotificationService: Permission denied');
        return false;
      }

      console.log('NotificationService: Permission granted');

      // Initialize messaging
      await this.initialize();

      // Get and register token
      const token = await this.getAndRegisterToken();

      return !!token;
    } catch (error) {
      console.error('NotificationService: Error requesting permission:', error);
      return false;
    }
  }

  /**
   * Initialize Firebase Messaging
   */
  private async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Wait for Firebase app to be initialized
      const app = getApp();
      this.messaging = getMessaging(app);
      this.isInitialized = true;

      // Set up foreground message handler
      this.setupForegroundHandler();

      // Set up service worker message handler
      this.setupServiceWorkerMessageHandler();

      console.log('NotificationService: Initialized');
    } catch (error) {
      console.error('NotificationService: Error initializing:', error);
      throw error;
    }
  }

  /**
   * Get FCM token and register with backend
   */
  private async getAndRegisterToken(): Promise<string | null> {
    if (!this.messaging) {
      console.error('NotificationService: Messaging not initialized');
      return null;
    }

    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('NotificationService: Service worker registered');

      // Get FCM token
      const token = await getToken(this.messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        console.log('NotificationService: FCM token received');
        this.currentToken = token;

        // Register with backend
        await this.sendTokenToBackend(token);
      } else {
        console.log('NotificationService: No token available');
      }

      return token;
    } catch (error) {
      console.error('NotificationService: Error getting token:', error);
      return null;
    }
  }

  /**
   * Send FCM token to backend for storage
   */
  private async sendTokenToBackend(token: string): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      console.log('NotificationService: No user signed in, skipping token registration');
      return;
    }

    try {
      const idToken = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/notifications/register-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          token: token,
          platform: 'web',
          user_agent: navigator.userAgent,
        }),
      });

      if (response.ok) {
        console.log('NotificationService: Token registered with backend');
      } else {
        console.error('NotificationService: Token registration failed:', response.status);
      }
    } catch (error) {
      console.error('NotificationService: Error sending token to backend:', error);
    }
  }

  /**
   * Unregister FCM token from backend (call on sign out)
   */
  async unregisterToken(): Promise<void> {
    if (!this.currentToken) {
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      return;
    }

    try {
      const idToken = await user.getIdToken();

      await fetch(`${API_BASE_URL}/notifications/unregister-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          token: this.currentToken,
        }),
      });

      console.log('NotificationService: Token unregistered');
      this.currentToken = null;
    } catch (error) {
      console.error('NotificationService: Error unregistering token:', error);
    }
  }

  /**
   * Set up handler for foreground messages
   */
  private setupForegroundHandler(): void {
    if (!this.messaging) {
      return;
    }

    onMessage(this.messaging, (payload) => {
      console.log('NotificationService: Foreground message received:', payload);

      // Show notification using the Notification API
      const title = payload.notification?.title || 'Comments Ready!';
      const body = payload.notification?.body || 'Your comments are ready for review';
      const sessionId = payload.data?.sessionId;

      // Show browser notification
      const notification = new Notification(title, {
        body: body,
        icon: '/logo-white-192.png',
        tag: sessionId || 'comments-ready',
        data: { sessionId },
      });

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        if (sessionId) {
          window.location.href = `/session/${sessionId}`;
        }
        notification.close();
      };
    });
  }

  /**
   * Set up handler for messages from service worker
   */
  private setupServiceWorkerMessageHandler(): void {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'NAVIGATE_TO_SESSION' && event.data?.sessionId) {
        console.log('NotificationService: Navigating to session from SW message:', event.data.sessionId);
        window.location.href = `/session/${event.data.sessionId}`;
      }
    });
  }

  /**
   * Re-register token after user signs in
   * Call this when auth state changes to signed in
   */
  async onUserSignIn(): Promise<void> {
    if (!this.isSupported()) {
      return;
    }

    // Check if we have permission
    if (Notification.permission !== 'granted') {
      return;
    }

    // Re-register token if we have one
    if (this.currentToken) {
      await this.sendTokenToBackend(this.currentToken);
    } else {
      // Try to get a new token
      await this.initialize();
      await this.getAndRegisterToken();
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Export class for testing
export { NotificationService };
